import { ChangeDetectionStrategy, Component, computed, effect, input, output, viewChildren } from '@angular/core';
import { CANVAS_IMAGE_UPLOAD_SERVICE, CanvasComponent } from '@examdojo/canvas';
import { EMPTY } from 'rxjs';

export interface CanvasExportResult {
  attemptId: string;
  files: File[];
}

@Component({
  selector: 'dojo-hidden-canvas-exporter',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CanvasComponent],
  providers: [
    {
      provide: CANVAS_IMAGE_UPLOAD_SERVICE,
      useValue: { uploadImage: () => EMPTY },
    },
  ],
  template: `
    <div class="pointer-events-none fixed -left-[9999px] top-0 h-[0px] w-[0px] opacity-0">
      @for (attemptId of attemptIds(); track attemptId) {
        <dojo-canvas [roomId]="buildRoomId(attemptId)" [isReadonly]="true" class="h-full w-full" />
      }
    </div>
  `,
  host: { class: 'contents' },
})
export class HiddenCanvasExporterComponent {
  constructor() {
    effect(() => {
      const ready = this.allCanvasesReady();
      if (ready) {
        this.exportAllCanvases();
      }
    });
  }

  readonly userId = input.required<string>();
  readonly attemptIds = input<string[]>([]);

  readonly exportsReady = output<CanvasExportResult[]>();

  private readonly canvasComponents = viewChildren(CanvasComponent);

  private readonly allCanvasesReady = computed(() => {
    const attemptIds = this.attemptIds();
    const canvases = this.canvasComponents();

    if (attemptIds.length === 0 || canvases.length !== attemptIds.length) {
      return false;
    }

    return canvases.every((canvas) => canvas.ready());
  });

  buildRoomId(attemptId: string): string {
    return `${this.userId()}/${attemptId}`;
  }

  private async exportAllCanvases(): Promise<void> {
    const attemptIds = this.attemptIds();
    const canvases = this.canvasComponents();

    if (attemptIds.length === 0 || canvases.length !== attemptIds.length) {
      return;
    }

    const results = await Promise.allSettled(
      canvases.map(async (canvas, index) => {
        const files = await canvas.exportCanvasPagesAsImages();
        return { attemptId: attemptIds[index], files };
      }),
    );

    const exports = results
      .filter((result): result is PromiseFulfilledResult<CanvasExportResult> => result.status === 'fulfilled')
      .map((result) => result.value);

    this.exportsReady.emit(exports);
  }
}
